import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Smartphone, AlertCircle, Clock } from "lucide-react"
import { useAuth } from "../auth/AuthContext"
import { api } from "../api/client"
import Sidebar from "./Sidebar"
import Topbar from "./Topbar"
import ToastPoller from "../notifications/ToastPoller.jsx"

// ═══════════════════════════════════════════════════════════════
// Banner de verificación de celular (rediseñado)
// ═══════════════════════════════════════════════════════════════
function PhoneVerifyBanner() {
  const { user, refreshUser } = useAuth()
  const [fase, setFase] = useState("idle")   // idle | sending | otp | verifying | done | error
  const [waLink, setWaLink] = useState("")
  const [otp, setOtp] = useState("")
  const [err, setErr] = useState(null)
  const [msg, setMsg] = useState(null)
  const [dismissed, setDismissed] = useState(false)

  if (!user || user.phoneVerified !== false || dismissed) return null

  const pedirCodigo = async () => {
    setFase("sending"); setErr(null); setMsg(null)
    try {
      const r = await api.resendPhoneVerification()
      if (r.alreadyVerified) {
        await refreshUser()
        return
      }
      setWaLink(r.waLink)
      setFase("otp")
      setMsg("Abrí WhatsApp y enviá el mensaje pre-rellenado. Vas a recibir el código en segundos.")
    } catch (e) {
      setErr(e.message || "Error pidiendo el código")
      setFase("error")
    }
  }

  const verificar = async () => {
    if (!otp || otp.length !== 6) { setErr("Ingresá los 6 dígitos"); return }
    setFase("verifying"); setErr(null)
    try {
      await api.verifyPhoneOtpAuth(otp)
      await refreshUser()
      setFase("done")
      setMsg("¡Celular verificado! Este banner desaparecerá en un momento.")
    } catch (e) {
      setErr(e.message || "Código incorrecto")
      setFase("otp")
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-amber-200 bg-gradient-to-r from-amber-50 via-amber-50 to-white"
    >
      <div className="flex items-center gap-4 px-4 sm:px-6 py-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-amber-700" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-amber-900">Verificá tu número de celular</div>
            <div className="text-xs text-amber-800/80 mt-0.5">
              {fase === "idle" && "Tu cuenta quedó sin verificar. Verificalo ahora para recibir notificaciones y OTPs."}
              {fase === "sending" && "Generando nuevo código..."}
              {(fase === "otp" || fase === "verifying" || fase === "error") && (msg || "Seguí los pasos:")}
              {fase === "done" && msg}
            </div>
          </div>
        </div>

        {fase === "idle" && (
          <div className="flex items-center gap-2">
            <button
              onClick={pedirCodigo}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors shadow-sm"
            >
              Verificar ahora
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="px-3 py-2 rounded-xl text-xs text-amber-800/60 hover:bg-amber-100 font-semibold transition-colors"
            >
              Después
            </button>
          </div>
        )}

        {(fase === "otp" || fase === "verifying" || fase === "error") && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => window.open(waLink, "_blank")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1ea952] transition-colors shadow-sm"
            >
              <Smartphone className="w-4 h-4" />
              Abrir WhatsApp
            </button>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              maxLength={6}
              placeholder="000000"
              className="w-[110px] px-3 py-2 text-center text-base tracking-[0.4em] font-mono rounded-xl border-2 border-amber-200 focus:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-500/10 bg-white"
            />
            <button
              onClick={verificar}
              disabled={fase === "verifying" || otp.length !== 6}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {fase === "verifying" ? "Verificando..." : "Verificar"}
            </button>
          </div>
        )}
      </div>

      {err && (
        <div className="flex items-center gap-2 px-4 sm:px-6 pb-3 text-xs text-danger">
          <AlertCircle className="w-3.5 h-3.5" />
          {err}
        </div>
      )}
    </motion.div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Banner KYC en revisión manual (F11.88)
// ═══════════════════════════════════════════════════════════════
function KycStatusBanner() {
  const { user } = useAuth()
  if (!user || user.kycStatus !== "MANUAL_REVIEW") return null
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-amber-200 bg-gradient-to-r from-amber-50 via-amber-50 to-white"
    >
      <div className="flex items-center gap-4 px-4 sm:px-6 py-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Clock className="w-5 h-5 text-amber-700" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-amber-900">
            Tu cuenta está en revisión manual
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            Un administrador revisará tu registro en las próximas 24h.
            Podés navegar por la plataforma, pero no usar servicios
            (topups, flujos) hasta que sea aprobada. Te avisaremos por WhatsApp.
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Layout principal
// ═══════════════════════════════════════════════════════════════
export default function Layout({ children, title }) {
  const { user } = useAuth()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "1"
    } catch { return false }
  })
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem("sidebar-collapsed", collapsed ? "1" : "0")
    } catch { /* ignore */ }
  }, [collapsed])

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Toast global de notificaciones (invisible, no afecta layout) */}
      <ToastPoller />

      {/* Sidebar (desktop + mobile drawer) */}
      <Sidebar
        user={user}
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      {/* Contenido */}
      <div
        className="transition-all duration-300"
        style={{ paddingLeft: "var(--sidebar-width, 0px)" }}
      >
        {/* Wrapper con margen dinámico según sidebar */}
        <div className={`transition-[margin] duration-300 ease-smooth ${collapsed ? "lg:ml-[72px]" : "lg:ml-[248px]"}`}>
          <Topbar
            onToggleMobile={() => setMobileOpen((v) => !v)}
            title={title}
          />

          <PhoneVerifyBanner />
          <KycStatusBanner />

          <main className="px-4 sm:px-6 py-6 sm:py-8 max-w-[1400px] mx-auto">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
